"use client";

import { DrawerPreview as Drawer } from "@base-ui/react/drawer";
import { Popover } from "@base-ui/react/popover";
import { toPlainDateString, type PlainDate } from "@sa-nu/shared-utils/date";
import { useId } from "react";

import type { buildAccommodationReservationProviderInput } from "../../../../../utils";
import { useIsMobile } from "../../../../_hooks/use-responsive-value";

import {
  CheckoutDatePickerContent,
  type CheckoutDatePickerMessages,
  type CheckoutDatePickerOnSelect,
} from "./_components/checkout-date-picker-content";

export type { CheckoutDatePickerOnSelect } from "./_components/checkout-date-picker-content";

export type CheckoutDatePickerDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accommodationId: string;
  /** タップした日 = CI 候補 */
  firstNight: PlainDate;
  provider: ReturnType<typeof buildAccommodationReservationProviderInput>;
  /** 予約可能範囲の先頭夜 */
  minNight: PlainDate;
  /** 予約可能範囲の末尾夜 (泊まれる最終の夜) */
  maxNight: PlainDate;
  /** CO 確定時。close は呼び出し側 (closeAll 等) が行う */
  onSelect: CheckoutDatePickerOnSelect;
  /**
   * PC Popover のアンカー要素 (「予約する」CTA)。Popover.Positioner の `anchor` に渡す。
   * room-detail 側が callback ref で取得した state 要素をそのまま受け取る
   * (関数ラッパーだと Base-UI が anchor を再解決せず効かないため、要素を直接渡す)。
   */
  anchorEl?: HTMLElement | null;
  /** テスト/Storybook で決定的な today にするため任意注入 (デフォルト: getCurrentDate()) */
  today?: PlainDate;
  messages?: CheckoutDatePickerMessages;
};

/**
 * チェックアウト日選択のレスポンシブ entry (controlled)。
 * 画面幅で出し分ける:
 * - モバイル (< 768px): Base-UI Drawer (bottom sheet) + scroll カレンダー
 * - PC (>= 768px): Base-UI Popover (「予約する」CTA にアンカー) + scroll カレンダー
 *
 * export 名・props は維持し reservation-calendar 側の配線を無改変にする。
 */
export function CheckoutDatePickerDrawer(props: CheckoutDatePickerDrawerProps) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <CheckoutDateDrawer {...props} />
  ) : (
    <CheckoutDatePopover {...props} />
  );
}

/** モバイル: Base-UI Drawer (bottom sheet)。シェル構造は room-detail-drawer を踏襲。 */
function CheckoutDateDrawer({
  open,
  onOpenChange,
  firstNight,
  ...contentProps
}: CheckoutDatePickerDrawerProps) {
  const titleId = useId();
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        {/* forceRender: ネストされた Drawer は既定で Backdrop を描画しないが、
            outside タップで「最前面の Drawer だけ閉じる」判定はクリック対象が
            自分の Backdrop であることを要求するため、明示的に描画する */}
        <Drawer.Backdrop
          forceRender
          className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0"
        />
        <Drawer.Viewport className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-[640px]">
          <Drawer.Popup
            aria-labelledby={titleId}
            // max-h ではなく h で固定する。カレンダー body は中身の高さに寄与しない
            // (absolute) ため、Popup の高さが不定だと flex-1 に配る余剰が 0 になり body が潰れる。
            // 高さの値は room-detail-drawer と揃える (部屋詳細と同じ全画面高)
            className="bg-dx-surface-base flex h-[calc(100dvh-60px)] w-full flex-col overflow-hidden rounded-t-[16px] pb-[env(safe-area-inset-bottom)] shadow-lg transition-transform duration-200 data-ending-style:translate-y-full data-starting-style:translate-y-full md:h-[calc(100dvh-80px)]"
            style={{
              transform:
                "translateY(calc(var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px)))",
            }}
          >
            {/* grip bar */}
            <div className="flex shrink-0 justify-center pt-[12px] pb-[8px]">
              <div className="bg-dx-border-default h-[4px] w-[40px] rounded-full" />
            </div>
            {/* body は open 中のみマウントし、firstNight を key にすることで
                開き直し / CI 変更のたびに selection state と fetch をリセットする */}
            {open && (
              <CheckoutDatePickerContent
                key={toPlainDateString(firstNight)}
                firstNight={firstNight}
                titleId={titleId}
                onRequestClose={() => onOpenChange(false)}
                {...contentProps}
              />
            )}
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/** PC: Base-UI Popover。「予約する」CTA ボタンの下端に合わせて上方向に表示する。 */
function CheckoutDatePopover({
  open,
  onOpenChange,
  firstNight,
  anchorEl,
  ...contentProps
}: CheckoutDatePickerDrawerProps) {
  const titleId = useId();
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Portal>
        {/* ビューポート全体を暗転させる backdrop。外側クリックでの close は
            Popover 既定の dismiss 挙動が担う。room-detail の Drawer (z-50) より
            後に Portal されるため DOM 順で前面に出る */}
        <Popover.Backdrop className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0" />
        <Popover.Positioner
          anchor={anchorEl ?? undefined}
          // anchor (「予約する」CTA) は fixed な room-detail Drawer 内にあるため、
          // Positioner も fixed 方式にしないと座標系がズレてアンカーが効かない
          positionMethod="fixed"
          side="top"
          // Popover の下端を CTA ボタンの「下端」に合わせる (上端ではなく)。
          // side="top" は anchor 上端基準なので、ボタン高さ分の負オフセットで
          // 重ねて下端を揃える (offsetHeight 参照でボタン高さに非依存)。
          sideOffset={anchorEl ? -anchorEl.offsetHeight : 8}
          align="center"
          className="z-50"
        >
          <Popover.Popup
            aria-labelledby={titleId}
            // カレンダー body は absolute 配置で中身の高さに寄与しないため、Popup に
            // 高さを与えないと flex-1 が 0 になり潰れる。Figma の Popover 高さ (~608px) を
            // 基準にしつつ、低い画面では max-h でクランプする
            className="bg-dx-surface-base flex h-[600px] max-h-[calc(100dvh-120px)] w-[328px] flex-col overflow-hidden rounded-[16px] shadow-lg"
          >
            {open && (
              <CheckoutDatePickerContent
                key={toPlainDateString(firstNight)}
                firstNight={firstNight}
                titleId={titleId}
                onRequestClose={() => onOpenChange(false)}
                {...contentProps}
              />
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
